import { useEffect, useState } from 'react';
import type { ImageSourcePropType, ImageURISource } from 'react-native';
import { Image } from 'react-native';

import type { RelativeSizeCalculatorProps } from '../Image.types';

const calculateRelativeHeight = ({
  width,
  actualWidth,
  actualHeight,
}: RelativeSizeCalculatorProps): number =>
  width ? (actualHeight / actualWidth) * width : 0;

const calculateRelativeWidth = ({
  height,
  actualWidth,
  actualHeight,
}: RelativeSizeCalculatorProps): number =>
  height ? (actualWidth / actualHeight) * height : 0;

export const useImageSizeCalculator = (
  source: ImageSourcePropType,
): ImageSourcePropType => {
  const {
    uri,
    width: providedWidth,
    height: providedHeight,
  } = (source as ImageURISource) || {};
  const [width, setWidth] = useState<number | undefined>(providedWidth);
  const [height, setHeight] = useState<number | undefined>(providedHeight);

  useEffect(() => {
    if (!uri) return;

    if (providedHeight && providedWidth) return;
    let isMounted = true;

    Image.getSize(uri, (actualWidth, actualHeight) => {
      if (!isMounted) return;

      const size = { actualWidth, actualHeight };

      setWidth(
        providedWidth && !providedHeight
          ? calculateRelativeHeight({ ...size, width: providedWidth })
          : providedWidth ?? actualWidth,
      );

      setHeight(
        providedHeight && !providedWidth
          ? calculateRelativeWidth({ ...size, height: providedHeight })
          : providedHeight ?? actualHeight,
      );
    });

    return () => {
      isMounted = false;
    };
  }, [uri, providedWidth, providedHeight]);

  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  return ((source as ImageURISource) ?? {}).uri
    ? { width, height, uri }
    : source;
};
